import { forwardRef, useImperativeHandle, useRef, useMemo } from 'react'

import { Icons, LoadingTurbo } from 'atoms'
import { CustomBottomSheet, BottomSheetModal } from 'molecules'
import {
  useBreakpointValue,
  Menu,
  VStack,
  Heading,
  Text,
  useDisclose,
  HStack,
  Center,
  Pressable,
  IPressableProps
} from 'native-base'
import { GestureResponderEvent } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

type Option = {
  value: string | number
  label: string
}

type DropdownSheetProps = {
  placeholder: string
  options: Option[]
  value?: string | number
  onSelect: (value?: string | number) => void
  onClear?: (option: Option) => void
  clearable?: boolean
  title?: string
  triggerProps?: IPressableProps
}

export type DropdownSheetRefProps = {
  open: () => void
  close: () => void
}

export const DropdownSheet = forwardRef<DropdownSheetRefProps, DropdownSheetProps>(
  (
    {
      placeholder,
      options,
      value,
      onSelect,
      onClear,
      title = 'Selecione',
      triggerProps,
      clearable = false
    },
    ref
  ) => {
    const isMobile = useBreakpointValue({ base: true, lg: false })

    const { isOpen, onClose, onOpen } = useDisclose(false)

    const bottomSheetRef = useRef<BottomSheetModal>(null)

    useImperativeHandle(
      ref,
      () => ({
        open: () => (isMobile ? bottomSheetRef.current?.present() : onOpen()),
        close: () => (isMobile ? bottomSheetRef.current?.dismiss() : onClose())
      }),
      [isMobile, onClose, onOpen]
    )

    const selectedOption = useMemo(() => {
      return options.find((option) => option.value === value)
    }, [options, value])

    const handleSelect = (option: Option) => {
      onSelect(option.value)

      if (isMobile) {
        bottomSheetRef.current?.dismiss()
      } else {
        onClose()
      }
    }

    const handleClear = (e: GestureResponderEvent) => {
      e.stopPropagation()

      if (selectedOption) {
        if (onClear) {
          onClear(selectedOption)
        } else {
          onSelect(undefined)
        }
      }
    }

    const renderTrigger = (menuTriggerProps?: Partial<IPressableProps>) => {
      return (
        <Pressable
          w={triggerProps?.w ?? '300px'}
          _pressed={{
            opacity: 0.8
          }}
          onPress={isMobile ? () => bottomSheetRef.current?.present() : onOpen}
          h={triggerProps?.h ?? 10}
          bg={triggerProps?.bg ?? 'gray.30'}
          borderRadius={triggerProps?.borderRadius ?? '40px'}
          _disabled={{
            opacity: 0.6
          }}
          px={triggerProps?.px ?? 4}
          {...menuTriggerProps}
          {...triggerProps}>
          <HStack alignItems="center" h="full" justifyContent="space-between">
            <Text
              fontSize="16px"
              isTruncated
              color={triggerProps?.color ?? 'gray.400'}
              maxW={isMobile ? '80%' : 'auto'}>
              {selectedOption?.label ?? placeholder}
            </Text>

            <HStack alignItems="center" space={1}>
              {selectedOption && clearable && (
                <Pressable
                  onPress={handleClear}
                  justifyContent="center"
                  alignItems="center"
                  p={2}
                  borderRadius="full"
                  _pressed={{
                    opacity: 0.8
                  }}>
                  <Icons.CloseTab size={4} color={triggerProps?.color ?? 'gray.700'} />
                </Pressable>
              )}

              <Icons.ArrowDropDown size={5} color={triggerProps?.color ?? 'gray.700'} />
            </HStack>
          </HStack>
        </Pressable>
      )
    }

    const renderOptions = () => (
      <VStack w="full">
        {options?.length > 0 ? (
          options.map((option, index) => (
            <Menu.Item
              key={`${index}-${option.value}`}
              onPress={() => handleSelect(option)}
              py={2}
              my={1}
              px={6}
              borderRadius={20}
              backgroundColor={option.value === value ? 'gray.50' : 'transparent'}
              _hover={{ backgroundColor: 'gray.50' }}
              _pressed={{ backgroundColor: 'gray.50' }}>
              <Text fontSize="16px" lineHeight="24px" color="gray.500">
                {option.label}
              </Text>
            </Menu.Item>
          ))
        ) : (
          <Center mt={2}>
            <LoadingTurbo size={32} />
          </Center>
        )}
      </VStack>
    )

    if (isMobile) {
      return (
        <>
          {renderTrigger()}
          <CustomBottomSheet
            ref={bottomSheetRef}
            snapPoints={[450]}
            handleIndicatorStyle={{ backgroundColor: 'gray.400' }}>
            <VStack p={4} space={6} w="full" h="full" pt={6}>
              <Heading fontSize="18px" lineHeight="24px" textAlign="center">
                {title}
              </Heading>
              <ScrollView showsVerticalScrollIndicator={false}>{renderOptions()}</ScrollView>
            </VStack>
          </CustomBottomSheet>
        </>
      )
    }

    return (
      <Menu
        isOpen={isOpen}
        onClose={onClose}
        w={triggerProps?.w ?? '300px'}
        maxW={triggerProps?.maxW ?? '300px'}
        maxH={400}
        mt={2}
        p={2}
        bgColor="white"
        borderRadius={20}
        trigger={(triggerProps) => {
          delete triggerProps.onPress

          return renderTrigger(triggerProps)
        }}>
        <ScrollView showsVerticalScrollIndicator={false}>{renderOptions()}</ScrollView>
      </Menu>
    )
  }
)
